import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  RefreshCw,
  X,
  ChevronRight,
  AlertCircle,
  ShieldOff,
  RotateCcw,
} from 'lucide-react';
import { api } from '../../api/client';

type AccountStatus = 'active' | 'warming' | 'banned' | 'recovered' | 'idle';

interface TelegramAccount {
  id: string;
  username: string;
  status: AccountStatus;
  farm_name: string;
  owner: string;
  proxy: string;
  warming_day: number | null;
  last_action: string;
  history?: AccountHistoryEntry[];
  proxy_info?: ProxyInfo;
  warming_chart?: WarmingPoint[];
}

interface AccountHistoryEntry {
  timestamp: string;
  action: string;
  result: string;
}

interface ProxyInfo {
  host: string;
  port: number;
  protocol: string;
  country: string;
  latency_ms: number;
}

interface WarmingPoint {
  day: number;
  actions: number;
}

interface TelegramAccountsResponse {
  items: TelegramAccount[];
  stats: {
    total: number;
    active: number;
    warming: number;
    banned_today: number;
  };
}

const STATUS_BADGE: Record<AccountStatus, string> = {
  active: 'bg-green-100 text-green-800',
  warming: 'bg-yellow-100 text-yellow-800',
  banned: 'bg-red-100 text-red-800',
  recovered: 'bg-blue-100 text-blue-800',
  idle: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }: { status: AccountStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function WarmingBar({ day }: { day: number | null }) {
  if (day === null) return <span className="text-gray-400 text-xs">—</span>;
  const pct = Math.min((day / 30) * 100, 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500">Day {day}</span>
    </div>
  );
}

function DetailSlideOut({
  account,
  onClose,
  onForceRecover,
  onBan,
}: {
  account: TelegramAccount;
  onClose: () => void;
  onForceRecover: (id: string) => void;
  onBan: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-white shadow-xl flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-semibold text-base">@{account.username}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Info */}
          <section>
            <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Account Info</h3>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Status</dt>
                <dd><StatusBadge status={account.status} /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Farm</dt>
                <dd>{account.farm_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Owner</dt>
                <dd className="truncate max-w-[200px]">{account.owner}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Warming day</dt>
                <dd><WarmingBar day={account.warming_day} /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Last action</dt>
                <dd>{new Date(account.last_action).toLocaleString()}</dd>
              </div>
            </dl>
          </section>

          {/* Proxy info */}
          {account.proxy_info && (
            <section>
              <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Proxy</h3>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Host:Port</dt>
                  <dd className="font-mono text-xs">{account.proxy_info.host}:{account.proxy_info.port}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Protocol</dt>
                  <dd>{account.proxy_info.protocol}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Country</dt>
                  <dd>{account.proxy_info.country}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Latency</dt>
                  <dd>{account.proxy_info.latency_ms} ms</dd>
                </div>
              </dl>
            </section>
          )}

          {/* Warming chart */}
          {account.warming_chart && account.warming_chart.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Warming Progress</h3>
              <div className="flex items-end gap-1 h-20">
                {account.warming_chart.map((pt) => {
                  const max = Math.max(...account.warming_chart!.map((p) => p.actions), 1);
                  const h = Math.round((pt.actions / max) * 100);
                  return (
                    <div key={pt.day} className="flex flex-col items-center flex-1" title={`Day ${pt.day}: ${pt.actions}`}>
                      <div
                        className="w-full bg-yellow-300 rounded-t"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">Actions per warming day</p>
            </section>
          )}

          {/* History */}
          <section>
            <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Last 20 Actions</h3>
            {account.history && account.history.length > 0 ? (
              <ul className="space-y-1.5 text-xs">
                {account.history.slice(0, 20).map((h, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className="text-gray-400 whitespace-nowrap">
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                    <span className="text-gray-700">{h.action}</span>
                    <span className={`ml-auto whitespace-nowrap ${h.result === 'ok' ? 'text-green-600' : 'text-red-500'}`}>
                      {h.result}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">No history available</p>
            )}
          </section>
        </div>

        {/* Actions */}
        <div className="border-t p-4 flex gap-2">
          <button
            className="btn-secondary flex items-center gap-1 flex-1"
            onClick={() => onForceRecover(account.id)}
          >
            <RotateCcw size={14} />
            Force Recover
          </button>
          <button
            className="flex items-center gap-1 flex-1 justify-center px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium"
            onClick={() => onBan(account.id)}
          >
            <ShieldOff size={14} />
            Ban
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTelegramAccounts() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<TelegramAccount | null>(null);
  const [filterStatus, setFilterStatus] = useState<AccountStatus | ''>('');
  const [filterFarm, setFilterFarm] = useState('');
  const [filterOwner, setFilterOwner] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery<TelegramAccountsResponse>({
    queryKey: ['admin-telegram-accounts'],
    queryFn: () => api.get('/admin/telegram-accounts').then((r) => r.data),
    refetchInterval: 30_000,
  });

  const forceRecover = useMutation({
    mutationFn: (id: string) => api.post(`/admin/telegram-accounts/${id}/recover`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-telegram-accounts'] });
      setSelected(null);
    },
  });

  const banAccount = useMutation({
    mutationFn: (id: string) => api.post(`/admin/telegram-accounts/${id}/ban`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-telegram-accounts'] });
      setSelected(null);
    },
  });

  const items = data?.items ?? [];
  const stats = data?.stats ?? { total: 0, active: 0, warming: 0, banned_today: 0 };

  const farms = [...new Set(items.map((a) => a.farm_name))].sort();

  const filtered = items.filter((a) => {
    if (filterStatus && a.status !== filterStatus) return false;
    if (filterFarm && a.farm_name !== filterFarm) return false;
    if (filterOwner && !a.owner.toLowerCase().includes(filterOwner.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {selected && (
        <DetailSlideOut
          account={selected}
          onClose={() => setSelected(null)}
          onForceRecover={(id) => forceRecover.mutate(id)}
          onBan={(id) => banAccount.mutate(id)}
        />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-blue-500" />
          <h1 className="text-xl font-semibold">Telegram Farm Accounts</h1>
          {isFetching && <RefreshCw size={14} className="animate-spin text-gray-400" />}
        </div>
        <button className="btn-secondary flex items-center gap-1" onClick={() => refetch()}>
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-gray-700' },
          { label: 'Active', value: stats.active, color: 'text-green-600' },
          { label: 'Warming', value: stats.warming, color: 'text-yellow-600' },
          { label: 'Banned Today', value: stats.banned_today, color: 'text-red-600' },
        ].map((s) => (
          <div key={s.label} className="card text-center py-3">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            className="input"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AccountStatus | '')}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="warming">Warming</option>
            <option value="banned">Banned</option>
            <option value="recovered">Recovered</option>
            <option value="idle">Idle</option>
          </select>

          <select
            className="input"
            value={filterFarm}
            onChange={(e) => setFilterFarm(e.target.value)}
          >
            <option value="">All farms</option>
            {farms.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>

          <input
            type="text"
            className="input"
            placeholder="Filter by owner email…"
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500">No accounts found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Username', 'Status', 'Farm', 'Owner', 'Proxy', 'Warming Day', 'Last Action', ''].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left font-medium text-gray-500"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">@{account.username}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={account.status} />
                    </td>
                    <td className="px-4 py-3">{account.farm_name}</td>
                    <td className="px-4 py-3 max-w-[160px] truncate text-gray-600">
                      {account.owner}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 max-w-[120px] truncate">
                      {account.proxy}
                    </td>
                    <td className="px-4 py-3">
                      <WarmingBar day={account.warming_day} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(account.last_action).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        className="text-blue-500 hover:text-blue-700 flex items-center gap-0.5 text-xs"
                        onClick={() => setSelected(account)}
                      >
                        Details <ChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
